import { createGetter, createSetter, defaultGetter, defaultSetter } from 'rewire-common';
import { ActionType } from 'rewire-ui';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import PagingModel from './PagingModel';
import { getPermittedSorts, MasterDataSettingsModel } from './MasterDataSettingsModel';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import PrioritySortMenuModel from '../ui/PrioritySortMenuModel';
import userDialogModel from '../schedule/UserDialogModel';
import { DocumentSchema } from '../schedule/DocumentDialogModel';
import { ISort } from '../DataTypes';
import { IAuditLogFilters } from '../audit/AuditLogListModel';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import UserSettingsItemsPanelView from './UserSettingsItemsPanelView';

const UserSchema = `
${DocumentSchema}
email
userSecurity {
  role {
    name
  }
}
phone {
  home
  mobile
  work
  fax
  workFax
}
`;

export default class UsersSettingsModel extends MasterDataSettingsModel {
  constructor() {
    super('Users', 'users', userDialogModel, [
      { label: 'Role', get: createGetter(['userSecurity', 'role', 'name']), set: createSetter(['userSecurity', 'role', 'name']) },
      { label: 'Email Address', get: defaultGetter('email'), set: defaultSetter('email') },
    ]);

    function load(filter?: { search?: string }): Promise<{ cursor?: string; totalCount: number }> {
      const permittedKeys = this.sortModel.getSortMenuItems().map((i: any) => i.name);
      const sorts = getPermittedSorts(this.sortModel.sorts, this._documentType, permittedKeys);
      return this._load(filter, sorts);
    }

    this.paging = new PagingModel(load.bind(this));
    this.sortModel = new PrioritySortMenuModel(
      () => this.paging.load(true),
      () => this.getSortMenuItems(),
    );
    this.filterMenuItems = [];
    this.ItemsPanelView = UserSettingsItemsPanelView;
  }

  getDefaultEditPanelHeaderOptionsItems() {
    const optionsItems = super
      .getDefaultEditPanelHeaderOptionsItems()
      .filter((f) => !['deleteUsers', 'archive', 'unArchive'].some((a) => a === f.name));

    optionsItems.push(
      ...[
        // {name: 'inviteUser', title: 'Invite User', onClick: this.inviteUser, closeOnClick: true,  disabled: () => !this._canAddItem || this.hasChanges || !this.selectedItem},
        {
          name: 'deleteUser',
          title: 'Delete User',
          icon: DeleteIcon,
          onClick: this.removeItem,
          closeOnClick: true,
          disabled: () => !this._canDeleteItem || this.hasChanges || !this.selectedItem,
        },
        {
          name: 'auditLog',
          title: 'View Audit Log',
          icon: LogIcon,
          onClick: this.loadAuditLogDialog,
          closeOnClick: true,
          disabled: () => !this.selectedItem,
        },
      ],
    );

    return optionsItems;
  }

  // inviteUser = async () => {
  //   const item = this.selectedItem;
  //   console.log(item);
  //   return;
  // }

  get access(): boolean {
    return mainPageModel.canAddUsers || mainPageModel.canEditUsers;
  }

  get _canAddItem(): boolean {
    return mainPageModel.canAddUsers;
  }

  get _canEditItem(): boolean {
    return mainPageModel.canEditUsers;
  }

  get _canDeleteItem(): boolean {
    return mainPageModel.canDeleteUsers;
  }

  loadAuditLogDialog = () => {
    const filter = { types: ['user'] } as IAuditLogFilters;
    if (this.selectedItem?.id) {
      filter.entityIds = [this.selectedItem.id];
    }
    auditLogDialogModel.load(filter);
  };

  getSortMenuItems(): { name: string; title: string; isCustom?: boolean }[] {
    return [
      { name: 'name', title: 'Name' },
      { name: 'email', title: 'Email' },
    ];
  }

  protected _handleOnSubmit() {
    if (!userDialogModel.error && this.state.selectedItem) {
      const obj = userDialogModel.mapToSave();
      obj.userSecurity = { role: { name: userDialogModel.form.field.role.value?.name } };
      Object.assign(this.state.selectedItem, obj);
    }
  }

  protected async _load(filter: { search?: string }, sort?: ISort) {
    const results = await DataQueries.getUsers(UserSchema, filter, sort, this.paging.itemsPerPage, this.paging.currentPage);
    this.setItems(results.data);
    return results;
  }

  get hasCheckedItems() {
    return this.items?.some((item: any) => item.checked);
  }

  get footerActions(): { [index: string]: ActionType } | undefined {
    return { undo: this.editor!.actions.undo, save: this.editor!.actions.save };
  }
}
